// flowsPractica.js — ESPEJO de los dos Flows con los que el alumno pide o reserva una clase por WhatsApp,
// para las vistas previas INTERACTIVAS de Mensajes automáticos (app de autoescuelas y simulador). PURO, sin red.
//
// De dónde sale cada cosa (y __tests__/flowsPractica.mirror.test.js lo compara con el backend):
//   - pantallas TIPO y DISPONIBILIDAD de «Pedir clase»          ← aws_lambdas/c_createFlow/flow_pedir_practica.json
//   - pantallas R_TIPO, R_PROFESOR, R_FECHA, R_CONFIRMAR        ← aws_lambdas/c_createFlow/flow_reservar_practica.json
//   - textos que rellena el endpoint (intro, mensajes, resumen) ← aws_lambdas/c_pedirPracticaFlow/reservaFlow.js
//   - días y horas libres, festivos, tope semanal, profesor      ← aws_lambdas/c_pedirPracticaFlow/reservaSlots.js
//     (aquí con un horario de EJEMPLO; el test comprueba que el backend saca los mismos huecos con él)
//   - lo que se guarda de la petición (normalizePeticion)       ← aws_lambdas/c_processMessage/pedirPracticaFlow.js
// Si cambias un texto o una regla en el backend, cámbialo aquí: el test falla hasta que coincidan.
//
// COPIA POR VALOR (fichero idéntico) en apps/conext-ventas/src/simulator/pages/autoescuela/mensajes/
// flowsPractica.js — un test del simulador compara los dos ficheros.

// ── «Pedir clase» (flow_pedir_practica.json) ────────────────────────────────────────────────────
export const PEDIR_UI = {
  TIPO: {
    title: "Pedir clase",
    heading: "¿Qué clase quieres?",
    label: "Tipo de clase",
    opciones: [
      { id: "ciudad", title: "Ciudad" },
      { id: "carretera", title: "Carretera" },
      { id: "autovia", title: "Autovía" },
      { id: "cualquiera", title: "Me da igual" },
    ],
    footer: "Continuar",
  },
  DISPONIBILIDAD: {
    title: "¿Cuándo te viene bien?",
    heading: "¿Cuándo te viene bien?",
    diasLabel: "Días que te van bien",
    dias: [
      { id: "lunes", title: "Lunes" },
      { id: "martes", title: "Martes" },
      { id: "miercoles", title: "Miércoles" },
      { id: "jueves", title: "Jueves" },
      { id: "viernes", title: "Viernes" },
      { id: "sabado", title: "Sábado" },
      { id: "domingo", title: "Domingo" },
    ],
    franjaLabel: "Franja horaria",
    franjas: [
      { id: "manana", title: "Mañana" },
      { id: "tarde", title: "Tarde" },
      { id: "cualquiera", title: "Me da igual" },
    ],
    notaLabel: "Algo que debamos saber",
    notaHelper: "Opcional",
    footer: "Enviar peticion",
  },
};

const TIPOS_PETICION = ["ciudad", "carretera", "autovia", "cualquiera"];
const DIAS_PETICION = PEDIR_UI.DISPONIBILIDAD.dias.map((d) => d.id);
const FRANJAS_PETICION = ["manana", "tarde", "cualquiera"];
const MAX_NOTA = 500;

/** pedirPracticaFlow.normalizePeticion: lo que se queda de lo que envía el alumno. */
export function normalizePeticion(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const tipo = TIPOS_PETICION.includes(raw.tipo) ? raw.tipo : "cualquiera";
  const franja = FRANJAS_PETICION.includes(raw.franja) ? raw.franja : "cualquiera";
  const dias = [];
  for (const d of Array.isArray(raw.dias) ? raw.dias : []) {
    if (DIAS_PETICION.includes(d) && !dias.includes(d)) dias.push(d);
  }
  const nota = typeof raw.nota === "string" ? raw.nota.trim().slice(0, MAX_NOTA) : "";
  return { tipo, dias, franja, nota };
}

// ── «Reservar clase» (flow_reservar_practica.json) ──────────────────────────────────────────────
export const RESERVAR_UI = {
  R_TIPO: {
    title: "Reservar clase",
    heading: "¿Qué clase quieres?",
    body: "Elige el tipo de clase y te enseñamos los días y horas libres. La clase queda reservada al momento.",
    label: "Tipo de clase",
    opciones: [
      { id: "ciudad", title: "Ciudad" },
      { id: "carretera", title: "Carretera" },
      { id: "autovia", title: "Autovía" },
    ],
    footer: "Ver calendario",
  },
  R_PROFESOR: { title: "Elige profesor", label: "Profesor", footer: "Ver calendario" },
  R_FECHA: { title: "Elige dia y hora", diaLabel: "Dia", horaLabel: "Hora", footer: "Continuar" },
  R_CONFIRMAR: {
    title: "Confirma tu clase",
    heading: "¿Te la reservamos?",
    caption: "Si luego no puedes, cancélala con el botón del recordatorio.",
    footer: "Reservar clase",
  },
  R_AVISO: { title: "Reservar clase", footer: "Cerrar" },
};

// Textos que pone el endpoint (reservaFlow.js).
export const RESERVA_TEXTOS = {
  introProfesor: "¿Con quién quieres la clase? Solo salen los profesores con horas libres.",
  habitual: "Tu profesor habitual",
  diasLibres: (n) => `${n} ${n === 1 ? "día" : "días"} con horas libres`,
  meDaIgual: { id: "cualquiera", title: "Me da igual", description: "Te enseñamos las horas de todos" },
  tocaDia: "Toca un día del calendario para ver sus horas libres.",
  eligeHora: (ymd) => `${dayLabel(ymd)}. Elige la hora.`,
  sinHorasTitulo: "Ahora mismo no hay horas libres",
  sinHoras: (diasVista, maxSemana) => {
    const tope = maxSemana > 0 ? ` Recuerda que puedes reservar hasta ${maxSemana} ${maxSemana === 1 ? "clase" : "clases"} por semana.` : "";
    return `No quedan horas libres en los próximos ${diasVista} días.${tope} Escríbenos por este chat y te buscamos un hueco.`;
  },
};

export const TIPOS_RESERVA = { ciudad: "Ciudad", carretera: "Carretera", autovia: "Autovía" };
export const TIPO_POR_DECIDIR = "por_definir";

// ── Fechas (reservaSlots.js), en hora local de la autoescuela ───────────────────────────────────
const DIAS_ES = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const SIN_TILDE = { "miércoles": "miercoles", "sábado": "sabado" };
export const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const FESTIVOS_FIJOS = ["01-01", "01-06", "05-01", "08-15", "10-12", "11-01", "12-06", "12-08", "12-25"];
const MAX_OPCIONES_HORA = 100;

const pad = (n) => String(n).padStart(2, "0");
export function addDays(ymd, n) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
export function weekday(ymd) {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 domingo
}
export function weekKey(ymd) {
  const wd = weekday(ymd);
  return addDays(ymd, wd === 0 ? -6 : 1 - wd);
}
/** «Jueves 8 de octubre». */
export function dayLabel(ymd) {
  const [, m, d] = ymd.split("-").map(Number);
  const nombre = DIAS_ES[weekday(ymd)];
  return `${nombre.charAt(0).toUpperCase()}${nombre.slice(1)} ${d} de ${MESES[m - 1]}`;
}
function pascua(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const mes = Math.floor((h + l - 7 * m + 114) / 31), dia = ((h + l - 7 * m + 114) % 31) + 1;
  return `${y}-${pad(mes)}-${pad(dia)}`;
}
export function esFestivoNacional(ymd) {
  if (FESTIVOS_FIJOS.includes(ymd.slice(5))) return true;
  return ymd === addDays(pascua(Number(ymd.slice(0, 4))), -2);
}
const toMin = (hhmm) => { const [h, m] = String(hhmm).split(":").map(Number); return h * 60 + m; };
const hhmm = (min) => `${pad(Math.floor(min / 60) % 24)}:${pad(min % 60)}`;
const diaN = (ymd) => { const [y, m, d] = ymd.split("-").map(Number); return Date.UTC(y, m - 1, d) / 86400000; };
const diasEntre = (a, b) => diaN(b) - diaN(a);
const firstName = (s) => String(s || "").trim().split(/\s+/)[0] || "";

// ── Reglas (reservaSlots.reservaConfig) ─────────────────────────────────────────────────────────
export const RESERVA_DEFAULTS = { duracion_min: 60, antelacion_horas: 24, dias_vista: 14, max_por_semana: 2, profesor: "suyo", tipo_clase: "alumno" };
export function reservaConfig(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const int = (v, min, max, def) => (Number.isInteger(v) && v >= min && v <= max ? v : def);
  return {
    duracion_min: int(r.duracion_min, 15, 240, RESERVA_DEFAULTS.duracion_min),
    antelacion_horas: int(r.antelacion_horas, 0, 336, RESERVA_DEFAULTS.antelacion_horas),
    dias_vista: int(r.dias_vista, 1, 60, RESERVA_DEFAULTS.dias_vista),
    max_por_semana: int(r.max_por_semana, 0, 21, RESERVA_DEFAULTS.max_por_semana),
    tipo_clase: ["alumno", "profesor"].includes(r.tipo_clase) ? r.tipo_clase : RESERVA_DEFAULTS.tipo_clase,
    profesor: ["suyo", "elige", "cualquiera"].includes(r.profesor)
      ? r.profesor
      : (r.solo_su_profesor === false ? "cualquiera" : RESERVA_DEFAULTS.profesor),
  };
}

// ── El EJEMPLO de la vista previa ───────────────────────────────────────────────────────────────
// «Ahora» es el lunes 5 de octubre de 2026 a las 10:24 (la hora de las burbujas). El alumno, Pablo, tiene
// de profesor habitual al primero de la lista y ya tiene una clase el miércoles 7 a las 17:00. Los profesores
// trabajan de lunes a viernes (el segundo también el sábado por la mañana) y tienen horas ya ocupadas por
// otros alumnos, para que el calendario no salga todo libre. El 12 de octubre es festivo nacional.
export const AHORA_EJEMPLO = { ymd: "2026-10-05", hhmm: "10:24" };
export const ALUMNO_EJEMPLO = { alumno_id: "al_pablo", instructor_id: "p0", nombre: "Pablo" };

const HORARIO_TIPO = [
  { lunes: [{ start: "09:00", end: "14:00" }, { start: "16:00", end: "20:00" }], martes: [{ start: "09:00", end: "14:00" }, { start: "16:00", end: "20:00" }], "miércoles": [{ start: "09:00", end: "14:00" }, { start: "16:00", end: "20:00" }], jueves: [{ start: "09:00", end: "14:00" }, { start: "16:00", end: "20:00" }], viernes: [{ start: "09:00", end: "14:00" }] },
  { lunes: [{ start: "08:00", end: "14:00" }], martes: [{ start: "08:00", end: "14:00" }], "miércoles": [{ start: "15:00", end: "21:00" }], jueves: [{ start: "08:00", end: "14:00" }], viernes: [{ start: "08:00", end: "14:00" }], "sábado": [{ start: "09:00", end: "13:00" }] },
  { lunes: [{ start: "16:00", end: "21:00" }], martes: [{ start: "16:00", end: "21:00" }], "miércoles": [{ start: "16:00", end: "21:00" }], jueves: [{ start: "16:00", end: "21:00" }], viernes: [{ start: "16:00", end: "20:00" }] },
];

/**
 * Datos del ejemplo con los nombres de los profesores de la autoescuela (hasta 3; sin nombres, Marta y Luis).
 * Prácticas en hora local: { instructor_id, alumno_id, ymd, start: "HH:MM", end: "HH:MM", status }.
 */
export function ejemploReserva(nombresProfesores = []) {
  const lista = (Array.isArray(nombresProfesores) ? nombresProfesores : []).map((n) => firstName(n)).filter(Boolean);
  const nombres = (lista.length ? lista : ["Marta", "Luis"]).slice(0, HORARIO_TIPO.length);
  const ids = nombres.map((_, i) => `p${i}`);
  const schedules = Object.fromEntries(ids.map((id, i) => [id, { schedule: HORARIO_TIPO[i] }]));
  const practicas = [];
  // Horas ya cogidas por otros alumnos: un patrón fijo por día y profesor (de 8 a 20 h).
  for (let i = 0; i <= 62; i++) {
    const ymd = addDays(AHORA_EJEMPLO.ymd, i);
    ids.forEach((id, k) => {
      for (let h = 8; h <= 20; h++) {
        if ((i * 7 + h + k * 2) % 3 === 0) practicas.push({ instructor_id: id, alumno_id: `otro_${k}`, ymd, start: `${pad(h)}:00`, end: `${pad(h + 1)}:00`, status: "programada" });
      }
    });
  }
  const propia = { instructor_id: "p0", alumno_id: ALUMNO_EJEMPLO.alumno_id, ymd: "2026-10-07", start: "17:00", end: "18:00", status: "programada" };
  practicas.push(propia);
  return {
    schedules,
    nombres: Object.fromEntries(ids.map((id, i) => [id, nombres[i]])),
    alumno: ALUMNO_EJEMPLO,
    practicas,
    practicasAlumno: [propia],
  };
}

function rangesFor(row, ymd) {
  if (!row || esFestivoNacional(ymd)) return [];
  const nombre = DIAS_ES[weekday(ymd)];
  const list = (row.schedule && (row.schedule[nombre] || row.schedule[SIN_TILDE[nombre]])) || [];
  return list.filter((r) => r && r.start < r.end);
}

const absMin = (ymd, min) => diasEntre(AHORA_EJEMPLO.ymd, ymd) * 1440 + min;
const solapa = (a1, a2, b1, b2) => a1 < b2 && b1 < a2;

function eligibleInstructors({ schedules, alumno, cfg, soloInstructor }) {
  const ids = Object.keys(schedules || {});
  if (soloInstructor && ids.includes(soloInstructor)) return [soloInstructor];
  const suyo = alumno && alumno.instructor_id;
  if (cfg.profesor === "suyo" && suyo && ids.includes(suyo)) return [suyo];
  return ids;
}

/** reservaSlots.freeSlotsForDay: una hora por inicio, prefiriendo al profesor del alumno. [{ instructor_id, ymd, hhmm, fin }] */
export function freeSlotsForDay({ ymd, ej, cfg: rawCfg, soloInstructor }) {
  const cfg = reservaConfig(rawCfg);
  const { schedules, practicas, alumno } = ej;
  const minStart = absMin(AHORA_EJEMPLO.ymd, toMin(AHORA_EJEMPLO.hhmm)) + cfg.antelacion_horas * 60;
  const ocupadas = (pred) => practicas.filter((p) => p.status !== "cancelada" && pred(p)).map((p) => [absMin(p.ymd, toMin(p.start)), absMin(p.ymd, toMin(p.end))]);
  const delAlumno = ocupadas((p) => p.alumno_id === alumno.alumno_id);
  const porHora = new Map();
  for (const id of eligibleInstructors({ schedules, alumno, cfg, soloInstructor })) {
    const busy = ocupadas((p) => p.instructor_id === id);
    for (const r of rangesFor(schedules[id], ymd)) {
      const fin = toMin(r.end);
      for (let s = toMin(r.start); s + cfg.duracion_min <= fin; s += cfg.duracion_min) {
        const a = absMin(ymd, s);
        const b = a + cfg.duracion_min;
        if (a < minStart) continue;
        if (busy.some(([x, y]) => solapa(a, b, x, y))) continue;
        if (delAlumno.some(([x, y]) => solapa(a, b, x, y))) continue;
        const prev = porHora.get(s);
        if (!prev || (alumno.instructor_id === id && prev.instructor_id !== alumno.instructor_id)) {
          porHora.set(s, { instructor_id: id, ymd, hhmm: hhmm(s), fin: hhmm(s + cfg.duracion_min) });
        }
      }
    }
  }
  return [...porHora.entries()].sort((a, b) => a[0] - b[0]).map(([, v]) => v).slice(0, MAX_OPCIONES_HORA);
}

function countByWeek(practicasAlumno) {
  const out = {};
  for (const p of practicasAlumno || []) {
    if (!p || p.status === "cancelada") continue;
    const k = weekKey(p.ymd);
    out[k] = (out[k] || 0) + 1;
  }
  return out;
}

/** reservaSlots.availableDays: [{ ymd, label, huecos }] */
export function availableDays({ ej, cfg: rawCfg, soloInstructor }) {
  const cfg = reservaConfig(rawCfg);
  const porSemana = countByWeek(ej.practicasAlumno);
  const out = [];
  for (let i = 0; i <= cfg.dias_vista; i++) {
    const ymd = addDays(AHORA_EJEMPLO.ymd, i);
    if (cfg.max_por_semana > 0 && (porSemana[weekKey(ymd)] || 0) >= cfg.max_por_semana) continue;
    const slots = freeSlotsForDay({ ymd, ej, cfg, soloInstructor });
    if (slots.length) out.push({ ymd, label: dayLabel(ymd), huecos: slots.length });
  }
  return out;
}

/** reservaSlots.instructoresConHueco: [{ instructor_id, dias }] */
export function instructoresConHueco({ ej, cfg }) {
  const out = [];
  for (const id of Object.keys(ej.schedules || {})) {
    const dias = availableDays({ ej, cfg, soloInstructor: id }).length;
    if (dias > 0) out.push({ instructor_id: id, dias });
  }
  return out;
}

/** horasOptions: «17:00 con Marta». */
export function horasOptions(slots, nombres) {
  return slots.map((s) => {
    const n = firstName(nombres[s.instructor_id]);
    return { id: `${s.instructor_id}|${s.ymd}T${s.hhmm}`, title: n ? `${s.hhmm} con ${n}`.slice(0, 30) : s.hhmm, slot: s };
  });
}

/** reservaFlow.resumenDe: «Clase de Ciudad el martes 6 de octubre a las 17:00 con Marta.» */
export function resumenDe({ tipo, slot, nombres }) {
  const dia = dayLabel(slot.ymd);
  const n = firstName(nombres[slot.instructor_id]);
  const cuando = `el ${dia.charAt(0).toLowerCase()}${dia.slice(1)} a las ${slot.hhmm}${n ? ` con ${n}` : ""}`;
  return tipo === TIPO_POR_DECIDIR ? `Clase práctica ${cuando}.` : `Clase de ${TIPOS_RESERVA[tipo] || "Ciudad"} ${cuando}.`;
}

/** Pantalla R_PROFESOR (modo «elige»): el habitual primero, cuántos días tiene cada uno y «Me da igual». */
export function profesoresElegibles({ ej, cfg }) {
  const habitual = ej.alumno && ej.alumno.instructor_id;
  const lista = instructoresConHueco({ ej, cfg }).sort((a, b) => (b.instructor_id === habitual) - (a.instructor_id === habitual));
  return [
    ...lista.map((p) => ({
      id: p.instructor_id,
      title: (firstName(ej.nombres[p.instructor_id]) || "Profesor").slice(0, 30),
      description: p.instructor_id === habitual ? RESERVA_TEXTOS.habitual : RESERVA_TEXTOS.diasLibres(p.dias),
    })),
    RESERVA_TEXTOS.meDaIgual,
  ];
}

/**
 * Por dónde pasa el alumno según las reglas (reservaFlow.handleReservaExchange):
 *   tipo   → solo si el tipo lo elige él (tipo_clase "alumno")
 *   profesor → solo en modo «elige» y con 2+ profesores con hueco (con uno, ese, sin preguntar)
 * Devuelve { pideTipo, pideProfesor, profesorFijo }.
 */
export function recorridoReserva({ ej, cfg: rawCfg }) {
  const cfg = reservaConfig(rawCfg);
  const pideTipo = cfg.tipo_clase !== "profesor";
  if (cfg.profesor !== "elige") return { pideTipo, pideProfesor: false, profesorFijo: "" };
  const lista = instructoresConHueco({ ej, cfg });
  if (lista.length >= 2) return { pideTipo, pideProfesor: true, profesorFijo: "" };
  return { pideTipo, pideProfesor: false, profesorFijo: lista.length ? lista[0].instructor_id : "" };
}

/** Pantalla R_FECHA: límites del calendario, días sin horas y, con un día elegido, sus horas. */
export function pantallaFecha({ ej, cfg, profesor = "", ymd = null }) {
  const c = reservaConfig(cfg);
  const soloInstructor = profesor && profesor !== "cualquiera" ? profesor : undefined;
  const dias = availableDays({ ej, cfg: c, soloInstructor });
  if (!dias.length) {
    return { aviso: { titulo: RESERVA_TEXTOS.sinHorasTitulo, texto: RESERVA_TEXTOS.sinHoras(c.dias_vista, c.max_por_semana) } };
  }
  const libres = new Set(dias.map((d) => d.ymd));
  const min = dias[0].ymd;
  const max = dias[dias.length - 1].ymd;
  const noDisponibles = [];
  for (let d = min; d <= max; d = addDays(d, 1)) if (!libres.has(d)) noDisponibles.push(d);
  const slots = ymd && libres.has(ymd) ? freeSlotsForDay({ ymd, ej, cfg: c, soloInstructor }) : [];
  return {
    min_date: min,
    max_date: max,
    unavailable_dates: noDisponibles,
    fecha_cita: slots.length ? ymd : "",
    mensaje: slots.length ? RESERVA_TEXTOS.eligeHora(ymd) : RESERVA_TEXTOS.tocaDia,
    horas: horasOptions(slots, ej.nombres),
    is_time_enabled: slots.length > 0,
  };
}
